// src/components/frontpage/ContactCard.jsx
// Contact details for an official or utility service.
// Two render modes:
//   - popout (default): click-to-open absolute popover (position:absolute), closes on outside
//     click + Escape. The original Directory pop-out affordance.
//   - inline (accordion): rendered in normal flow below the row (position:static, full width),
//     no outside-click dismiss — the contact toggles purely on its row click. Escape still closes.
// Shows "View group" link only when groupId is present in the contact data.

import React, { useRef, useEffect } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import PresencePanel from '../presence/PresencePanel';
import { contactToPresence } from '../presence/presenceSource';
import { openEntryStyle } from './DirectorySection';

const ContactCard = ({ contact, onClose, anchorRect, inline = false, onSuggestCorrection }) => {
  const theme = useTheme();
  const { t } = useTranslation('frontpage');
  const cardRef = useRef(null);

  // Outside-click dismiss is a pop-out affordance — NOT registered in inline (accordion) mode
  // (matches the DirectoryNav accordion fix). Escape-to-close applies in both modes.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);

    let handleMouseDown;
    if (!inline) {
      handleMouseDown = (e) => {
        if (cardRef.current && !cardRef.current.contains(e.target)) {
          onClose();
        }
      };
      document.addEventListener('mousedown', handleMouseDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (handleMouseDown) document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [onClose, inline]);

  return (
    <div
      ref={cardRef}
      style={inline ? {
        position: 'static',
        width: '100%',
        background: '#f7f9fc',
        borderRadius: '8px',
        padding: '8px 10px',
        marginTop: '4px',
        marginBottom: '4px',
        fontFamily: 'OpenSauceSans, sans-serif'
        // No border here (border-encloses-name correction, 16 Aug) — the parent ContactList
        // now wraps this and the row above it together in OpenEntryFrame, which supplies the
        // open-entry border around both. The popout branch below keeps its own border: it
        // already shows the name inside itself, so it doesn't need (or get) that wrapper.
      } : {
        position: 'absolute',
        left: '8px',
        top: anchorRect ? `${anchorRect.bottom + 4}px` : '0',
        zIndex: 200,
        background: 'white',
        borderRadius: '10px',
        padding: '14px 16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        width: '240px',
        fontFamily: 'OpenSauceSans, sans-serif',
        ...openEntryStyle(theme)
      }}
    >
      {inline ? (
        /* Inline (accordion) detail — the thin, listing-backed Presence render. The panel
           owns name · description · contacts · "View group", so the bespoke inline blocks
           below are used only by the popout style (out of scope for the Presence pass). */
        <PresencePanel
          source={contactToPresence(contact)}
          density="compact"
          onClose={onClose}
          onSuggestCorrection={onSuggestCorrection ? () => onSuggestCorrection(contact) : undefined}
        />
      ) : (
        <>
          {/* Name */}
          <div style={{
            fontWeight: '700',
            fontSize: '14px',
            color: '#1f2937',
            marginBottom: '6px'
          }}>
            {contact.name}
          </div>

          {/* Phone */}
          {contact.phone && (
            <div style={{
              fontSize: '13px',
              color: '#374151',
              marginBottom: '4px'
            }}>
              📞 {contact.phone}
            </div>
          )}

          {/* Languages */}
          {contact.languages && (
            <div style={{
              fontSize: '11px',
              color: '#9ca3af',
              marginBottom: contact.groupId ? '8px' : '0'
            }}>
              🌐 {contact.languages}
            </div>
          )}

          {/* View group link — only when groupId is present */}
          {contact.groupId && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent('ournet:navigate', {
                  detail: { view: 'group', groupId: contact.groupId }
                }));
                onClose();
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontFamily: 'OpenSauceSans, sans-serif',
                fontSize: '12px',
                fontWeight: '600',
                color: theme.primary,
                marginTop: '4px'
              }}
            >
              {t('view_group')} →
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default ContactCard;
